import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, Check, ArrowRight, Gift, ShoppingCart, BookOpen, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { offersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const NOTIF_ICONS = {
    offer: { icon: Gift, color: 'text-amber-600', bg: 'bg-amber-100' },
    new_offer: { icon: Gift, color: 'text-amber-600', bg: 'bg-amber-100' },
    best_practice: { icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-100' },
    order_status: { icon: Package, color: 'text-green-600', bg: 'bg-green-100' },
    order_call: { icon: ShoppingCart, color: 'text-orange-600', bg: 'bg-orange-100' },
};

export const NotificationBell = () => {
    const { isAuthenticated, isAdmin } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const sseRef = useRef(null);

    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated || isAdmin) return;
        setLoading(true);
        try {
            const response = await offersAPI.getMyNotifications();
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, isAdmin]);

    // Notification sound
    const playNotifSound = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.setValueAtTime(660, ctx.currentTime);
            osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12);
            gain.gain.setValueAtTime(0.25, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.35);
        } catch (e) { console.error('Audio notification error:', e); }
    }, []);

    // SSE connection for real-time notifications with auto-reconnect
    useEffect(() => {
        if (!isAuthenticated || isAdmin) return;

        fetchNotifications();

        const token = localStorage.getItem('token');
        if (!token) return;

        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        let retryTimeout = null;
        let closed = false;

        const connect = () => {
            if (closed) return;
            const evtSource = new EventSource(`${baseUrl}/api/sse/notifications?token=${token}`);
            sseRef.current = evtSource;

            evtSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'new_notification') {
                        setNotifications(prev => [data, ...prev]);
                        playNotifSound();
                        toast.info(data.title, {
                            description: data.message,
                            duration: 6000,
                        });
                    }
                } catch (e) { console.error('SSE parse error:', e); }
            };

            evtSource.onerror = () => {
                evtSource.close();
                if (!closed) {
                    retryTimeout = setTimeout(connect, 5000);
                }
            };
        };

        connect();

        return () => {
            closed = true;
            if (retryTimeout) clearTimeout(retryTimeout);
            if (sseRef.current) sseRef.current.close();
            sseRef.current = null;
        };
    }, [isAuthenticated, isAdmin, fetchNotifications, playNotifSound]);

    useEffect(() => {
        if (isOpen) fetchNotifications();
    }, [isOpen, fetchNotifications]);

    const handleMarkAsRead = async (notification) => {
        if (actionLoading) return;
        setActionLoading(true);
        const notifId = notification.id || notification.offer_id || notification.bp_id;
        const snapshot = notifications;
        setNotifications(prev => prev.filter(n => (n.id || n.offer_id || n.bp_id) !== notifId));
        try {
            await offersAPI.markNotificationRead(notifId);
        } catch (error) {
            console.error('Error marking notification as read:', error);
            setNotifications(snapshot);
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkAllAsRead = async () => {
        if (actionLoading) return;
        setActionLoading(true);
        const snapshot = notifications;
        const ids = notifications.map(n => n.id || n.offer_id || n.bp_id);
        setNotifications([]);
        try {
            await Promise.all(ids.map(id => offersAPI.markNotificationRead(id)));
        } catch (error) {
            console.error('Error marking all as read:', error);
            setNotifications(snapshot);
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewNotification = async (notification) => {
        await handleMarkAsRead(notification);
        setIsOpen(false);
        if (notification.type === 'order_status') {
            navigate('/dashboard/commandes');
        } else if (notification.type === 'best_practice') {
            navigate('/dashboard/bonnes-pratiques');
        } else {
            navigate('/dashboard/catalogues?tab=offers');
        }
    };

    if (!isAuthenticated || isAdmin) return null;

    const unreadCount = notifications.length;
    const getNotifStyle = (type) => NOTIF_ICONS[type] || NOTIF_ICONS.offer;

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    data-testid="notification-bell"
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <Badge
                            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs animate-pulse"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
                <Card className="border-0 shadow-none">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Bell className="h-5 w-5" />
                            Notifications
                        </CardTitle>
                        {unreadCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleMarkAllAsRead}
                                disabled={actionLoading}
                                className="text-xs"
                                data-testid="mark-all-read-btn"
                            >
                                <Check className="h-3 w-3 mr-1" />
                                Tout marquer lu
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="max-h-80 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-jungle"></div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Bell className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Aucune notification</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {notifications.map((notification) => {
                                    const style = getNotifStyle(notification.type);
                                    const Icon = style.icon;
                                    return (
                                        <div
                                            key={notification.id || notification.offer_id || notification.bp_id}
                                            className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg relative group cursor-pointer hover:bg-amber-100 transition-colors"
                                            onClick={() => handleViewNotification(notification)}
                                            data-testid="notification-item"
                                        >
                                            <div className={`p-2 ${style.bg} rounded-full`}>
                                                <Icon className={`h-4 w-4 ${style.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm text-jungle">
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {formatDateTime(notification.created_at)}
                                                </p>
                                                <div className="flex items-center gap-1 text-xs text-amber-600 mt-2 font-medium">
                                                    <span>Voir</span>
                                                    <ArrowRight className="h-3 w-3" />
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Marquer comme lu"
                                                disabled={actionLoading}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMarkAsRead(notification);
                                                }}
                                            >
                                                <Check className="h-3 w-3 text-green-600" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
